import { UseQueryResult } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown";
import EmptyPlaceholder from "../EmptyPlaceholder";

import { NameWithId } from "./types";

export { useCustomValueFilter } from "./hooks/useCustomValueFilter";
export type {
  UseCustomValueFilterOptions,
  UseCustomValueFilterReturn,
} from "./hooks/useCustomValueFilter";

const getItemText = (itemContent: ReactNode): string => {
  if (typeof itemContent === "string") {
    return itemContent;
  }

  if (
    typeof itemContent === "object" &&
    itemContent !== null &&
    "props" in itemContent &&
    typeof itemContent.props?.children === "string"
  ) {
    return itemContent.props.children;
  }

  return "";
};

const getDisplayText = ({
  hasValueWithName,
  valueName,
  isLoading,
  allowCustomValue,
  placeholder,
}: {
  hasValueWithName: boolean;
  valueName: string;
  isLoading: boolean;
  allowCustomValue: boolean;
  placeholder?: string;
}): string => {
  if (hasValueWithName) {
    return valueName;
  }

  if (isLoading && !allowCustomValue) {
    return "Loading...";
  }

  return placeholder || "";
};

const SelectContent: React.FC<{
  isLoading: boolean;
  isLoadingContent: boolean;
  isFetching: boolean;
  isError: boolean;
  allowCustomValue: boolean;
  hasItems: boolean;
  children: ReactNode;
}> = ({
  isLoading,
  isLoadingContent,
  isFetching,
  isError,
  allowCustomValue,
  hasItems,
  children,
}) => {
  if (isLoading || isLoadingContent || isFetching) {
    return (
      <div className="animate-pulse p-2 text-center text-gray-400">
        <span className="animate-pulse-opacity inline-block">Loading...</span>
      </div>
    );
  }

  if (isError && !allowCustomValue) {
    return <div className="p-2 text-center text-red-500">Error loading data</div>;
  }

  if (!hasItems) {
    return <EmptyPlaceholder message="No results found" className="mb-1" />;
  }

  return <>{children}</>;
};

const CustomValueDisplay: React.FC<{
  customValue: string;
  renderCustomValue?: (value: string) => ReactNode;
}> = ({ customValue, renderCustomValue }) => {
  if (renderCustomValue) {
    return <>{renderCustomValue(customValue)}</>;
  }

  if (customValue.includes(",")) {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Multiple</span>
        <span className="truncate">{customValue}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Create:</span>
      <span>{customValue}</span>
    </div>
  );
};

const ItemContent: React.FC<{
  isCustom: boolean;
  customValue?: string;
  renderCustomValue?: (value: string) => ReactNode;
  item: unknown;
  renderItem: (item: unknown) => ReactNode;
}> = ({ isCustom, customValue, renderCustomValue, item, renderItem }) => {
  if (isCustom && customValue) {
    return <CustomValueDisplay customValue={customValue} renderCustomValue={renderCustomValue} />;
  }

  return <>{renderItem(item)}</>;
};

const getContentAsString = (
  itemContent: ReactNode,
  isCustom: boolean,
  customValue?: string,
): string | undefined => {
  if (typeof itemContent === "string") {
    return itemContent;
  }

  if (isCustom) {
    return customValue;
  }

  return undefined;
};

export interface SelectedValue<T> {
  id: string;
  item: T;
}

export interface PaginatedSelectProps<T, TData extends Record<string, unknown>> {
  query: (page: number) => UseQueryResult<TData, unknown> | null;
  renderItem: (item: T) => ReactNode;
  getItemValue: (item: T) => string;
  placeholder?: string;
  onValueChange?: (value: SelectedValue<T>) => void;
  value?: SelectedValue<NameWithId>;
  className?: string;
  disabled?: boolean;
  onRemove?: () => void;
  extractData?: (data: TData) => { data: T[]; totalPages: number };
  filterComponent?: ReactNode;
  hidePageNumbers?: boolean;
  allowCustomValue?: boolean;
  onFilterChange?: (value: string) => void;
  filterValue?: string;
  renderCustomValue?: (value: string) => ReactNode;
}

const PaginatedSelect = <T, TData extends Record<string, unknown>>({
  query,
  renderItem,
  getItemValue,
  placeholder = "Select an option",
  onValueChange,
  value,
  className,
  disabled = false,
  hidePageNumbers = false,
  onRemove,
  extractData = (data: TData) => data as unknown as { data: T[]; totalPages: number },
  filterComponent,
  allowCustomValue = false,
  onFilterChange,
  filterValue = "",
  renderCustomValue,
}: PaginatedSelectProps<T, TData>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [internalFilterValue, setInternalFilterValue] = useState("");

  const queryResult = query(currentPage);
  const {
    data: queryData,
    isLoading: isLoadingContent,
    isFetching,
    isError,
  } = queryResult || { data: undefined, isLoading: false, isError: false };

  const isLoading = allowCustomValue ? false : isLoadingContent || isFetching;
  const { data: items = [], totalPages = 0 } = queryData
    ? extractData(queryData)
    : { data: [] as T[], totalPages: 0 };

  // Track filter value - use external if provided, otherwise internal
  const currentFilterValue = onFilterChange ? filterValue : internalFilterValue;

  // Check if current filter value matches any existing item
  const isCustomValue =
    allowCustomValue &&
    currentFilterValue &&
    !items.some(item => {
      const itemContent = renderItem(item);
      const itemText = getItemText(itemContent);
      return itemText.toLowerCase() === currentFilterValue.toLowerCase();
    });

  const customValueItem = useMemo(
    () =>
      isCustomValue
        ? ({
            __isCustom: true,
            __customValue: currentFilterValue,
          } as unknown as T)
        : null,
    [isCustomValue, currentFilterValue],
  );

  const allItems = useMemo(
    () => (customValueItem ? [customValueItem, ...items] : items),
    [customValueItem, items],
  );

  useEffect(() => {
    if (!isLoading && contentRef.current && items.length > 0) {
      const height = contentRef.current.offsetHeight;
      if (height !== contentHeight) {
        setContentHeight(height);
      }
    }
  }, [isLoading, items, contentHeight]);

  const handleItemClick = useCallback(
    (item: T) => {
      if (onValueChange) {
        // Check if this is a custom value
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isCustom = (item as any).__isCustom;
        if (isCustom) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const customValue = (item as any).__customValue;
          onValueChange({
            id: customValue,
            item: { id: customValue, name: customValue } as unknown as T,
          });
        } else {
          onValueChange({
            id: getItemValue(item),
            item: item,
          });
        }
      }
      setOpen(false);
    },
    [onValueChange, getItemValue],
  );

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (onRemove) {
        onRemove();
      }
    },
    [onRemove],
  );

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const hasNameProperty =
    typeof value?.item === "object" && value?.item !== null && "name" in value.item;

  const displayText = getDisplayText({
    hasValueWithName: hasNameProperty,
    valueName:
      hasNameProperty && value?.item && "name" in value.item
        ? (value.item as { name: string }).name
        : "",
    isLoading: isLoading ?? false,
    allowCustomValue,
    placeholder,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filterComponent]);

  // Update internal filter value when filterComponent changes
  useEffect(() => {
    if (!onFilterChange && filterComponent) {
      // Extract filter value from filterComponent if possible
      // This is a simplified approach - in practice you might need a more sophisticated way
      setInternalFilterValue("");
    }
  }, [filterComponent, onFilterChange]);

  return (
    <div className="relative w-full">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          ref={triggerRef}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
          disabled={disabled || (isLoading && !allowCustomValue)}
        >
          <div
            className={`flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-start ${
              hasNameProperty ? "text-black" : "text-muted-foreground"
            }`}
          >
            {displayText}
          </div>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 opacity-50"
          >
            <path
              d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-none p-0"
          align="start"
        >
          {filterComponent}
          <div
            ref={contentRef}
            style={isLoading && contentHeight ? { minHeight: `${contentHeight}px` } : {}}
            className="max-h-[200px] overflow-y-auto p-1"
          >
            <SelectContent
              isLoading={isLoading ?? false}
              isLoadingContent={isLoadingContent}
              isFetching={isFetching ?? false}
              isError={isError}
              allowCustomValue={allowCustomValue}
              hasItems={allItems.length > 0}
            >
              <>
                {allItems.map((item: T, index: number) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const isCustom = (item as any).__isCustom;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const customValue = isCustom ? (item as any).__customValue : undefined;

                  const itemContent = (
                    <ItemContent
                      isCustom={isCustom}
                      customValue={customValue}
                      renderCustomValue={renderCustomValue}
                      item={item}
                      // @ts-expect-error - TODO: fix this
                      renderItem={renderItem}
                    />
                  );
                  const contentAsString = getContentAsString(itemContent, isCustom, customValue);
                  const itemId = isCustom
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (item as any).__customValue
                    : getItemValue(item);
                  const isSelected = value?.id === itemId;

                  return (
                    <DropdownMenuItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${itemId}-${index}`}
                      className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${
                        isSelected ? "bg-gray-50 text-accent-foreground" : ""
                      } ${isCustom ? "border-t border-gray-200" : ""}`}
                      onSelect={e => {
                        e.preventDefault();
                        handleItemClick(item);
                      }}
                    >
                      <span className="block flex-1 truncate" title={contentAsString}>
                        {itemContent}
                      </span>
                      {isSelected && <Check className="ml-auto h-4 w-4 flex-shrink-0" />}
                    </DropdownMenuItem>
                  );
                })}
              </>
            </SelectContent>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-1 py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
                tabIndex={-1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {!hidePageNumbers && (
                <span className="text-sm">
                  Page {currentPage} of {totalPages || 1}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-8 w-8 p-0"
                tabIndex={-1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {onRemove && value?.id && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="absolute right-7 top-1/2 ml-2 h-4 w-4 -translate-y-1/2 p-0"
          disabled={disabled || isLoading}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

export default observer(PaginatedSelect);
